import React, { createContext, useContext, useState, ReactNode } from 'react';
import { OrderDetails, ContainerFillResult } from '../app/services/api/orderManagementClient';

interface ActiveOrderContextType {
  activeOrder: OrderDetails | null;
  containerData: ContainerFillResult | null;
  isActiveOrder: boolean;
  shippingDetails?: {
    deliveryMethod: string;
    containerCharge: string;
    totalDeliveryCharge?: string;
    isDeliveryChargeTotal?: boolean;
  };
  setShippingDetails: (details: { deliveryMethod: string; containerCharge: string; totalDeliveryCharge?: string; isDeliveryChargeTotal?: boolean }) => void; 
  setActiveOrder: (orderData: OrderDetails | null) => void;
  setContainerData: (containerInfo: ContainerFillResult | null) => void;
  clearActiveOrder: () => void;
}

const ActiveOrderContext = createContext<ActiveOrderContextType | undefined>(undefined);

interface ActiveOrderProviderProps {
  children: ReactNode;
}

export const ActiveOrderProvider: React.FC<ActiveOrderProviderProps> = ({ children }) => {
  const [activeOrder, setActiveOrder] = useState<OrderDetails | null>(null);
  const [containerData, setContainerData] = useState<ContainerFillResult | null>(null);
  const [shippingDetails, setShippingDetails] = useState({
    deliveryMethod: '-',
    containerCharge: '0.00',
    totalDeliveryCharge: '0.00',
    isDeliveryChargeTotal: false,
  });
  const isActiveOrder = activeOrder !== null;

  const clearActiveOrder = () => {
    sessionStorage.removeItem('orderly_active_order_meta');
    setActiveOrder(null);
    setContainerData(null);
    setShippingDetails({
      deliveryMethod: '-',
      containerCharge: '0.00',
      totalDeliveryCharge: '0.00',
      isDeliveryChargeTotal: false,
    });
  };

  const handleSetShippingDetails = (details: { deliveryMethod: string; containerCharge: string; totalDeliveryCharge?: string; isDeliveryChargeTotal?: boolean }) => {
    setShippingDetails({
      deliveryMethod: details.deliveryMethod,
      containerCharge: details.containerCharge,
      totalDeliveryCharge: details.totalDeliveryCharge ?? (details.containerCharge ?? '0.00'),
      isDeliveryChargeTotal: details.isDeliveryChargeTotal ?? false,
    });
  };

  const value: ActiveOrderContextType = {
    activeOrder,
    containerData,
    isActiveOrder,
    shippingDetails,
    setActiveOrder,
    setContainerData,
    setShippingDetails: handleSetShippingDetails,
    clearActiveOrder,
  };

  return (
    <ActiveOrderContext.Provider value={value}>
      {children}
    </ActiveOrderContext.Provider>
  );
};

export const useActiveOrder = (): ActiveOrderContextType => {
  const context = useContext(ActiveOrderContext);
  if (context === undefined) {
    throw new Error('useActiveOrder must be used within an ActiveOrderProvider');
  }
  return context;
};
